import { gql, useMutation } from "@apollo/client";

const REWRITE_INSPECTION_TOPICS = gql`
  mutation RewriteInspectionTopics($input: RewriteInspectionTopicsInput!) {
    rewriteInspectionTopics(input: $input) {
      success
      message
      operationId
      outcomes {
        taskId
        status
        message
      }
      templateFailures {
        templateId
        message
      }
    }
  }
`;

/** One edited tarkastuskohta row. `topicId` is null for a row the user added. */
export interface InspectionTopicRowInput {
  name: string;
  topicId?: string | null;
}

export interface RewriteInspectionTopicsInput {
  taskId: string;
  rows: InspectionTopicRowInput[];
  applyToWholeProject: boolean;
  applyToBaseTemplate: boolean;
}

/**
 * The result of a rewrite attempt on one tarkastustehtävä. `status` is one of
 * `"updated"`, `"skipped_started"`, `"skipped_relinked"` or `"failed"`.
 */
export interface InspectionTopicRewriteOutcome {
  taskId: string;
  status: string;
  message: string;
}

/** A Congrid template rewrite that failed during a hanke-wide run. */
export interface InspectionTopicRewriteTemplateFailure {
  templateId: string;
  message: string;
}

/**
 * `operationId` is set only for the enqueued (whole-project) path; `outcomes`
 * is empty there too, since results arrive through progress reporting instead
 * (see `useMyInitialisations`).
 */
export interface InspectionTopicRewriteResult {
  success: boolean;
  message: string;
  operationId: string | null;
  outcomes: InspectionTopicRewriteOutcome[];
  templateFailures: InspectionTopicRewriteTemplateFailure[];
}

export function useInspectionTopicRewrite() {
  const [mutate, { loading }] = useMutation<{
    rewriteInspectionTopics: InspectionTopicRewriteResult;
  }>(REWRITE_INSPECTION_TOPICS);

  return {
    loading,
    rewriteTopics: async (
      input: RewriteInspectionTopicsInput,
    ): Promise<InspectionTopicRewriteResult | undefined> => {
      const { data } = await mutate({ variables: { input } });
      return data?.rewriteInspectionTopics;
    },
  };
}
